/**
 * useAdminHealthCache — shared 30s TTL + optimistic revalidation for the
 * Command Centre's admin-facing health tiles.
 * Jul 2026
 *
 * Problem: `CIHealthBadge`, `PytestFlakeCard`, `PostmarkBudgetCard`, and any
 * future admin health tile each poll their own `/api/admin/*` endpoint on
 * mount + on a per-component interval. On a Command Centre that's mounted
 * for hours a day, four independent tiles + their intervals produce dozens
 * of duplicate fetches. On a slow/flaky connection each cold-fetch shows a
 * blank spinner while the founder waits.
 *
 * Fix: one module-scoped `Map<key, {data, ts, inFlight}>` with a 30s TTL.
 * • First mount for a key → fires the fetch, stores the result.
 * • Any later mount for the same key within 30s → returns the cached row
 *   synchronously (no spinner) AND fires a background revalidation.
 * • Concurrent mounts share the same in-flight Promise (dedup).
 * • On refetch, cache is updated + all subscribers are notified.
 *
 * Callers get `{data, loading, error, refetch}`. `data` is the last-known
 * good response — never null after the first successful fetch — so the
 * tile stays populated across route changes and network hiccups.
 *
 * Design intent: NOT a general-purpose SWR replacement. Scope is deliberately
 * limited to the Command Centre's `/api/admin/*` GETs so we don't drag in a
 * new dependency for four tiles.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../api/client';

const TTL_MS = 30 * 1000;

// Module-scoped shared cache. Deliberately survives component unmounts so
// the founder navigating to /admin/users and back doesn't re-fetch on
// return.
const cache = new Map(); // key → { data, ts, error, inFlight, subscribers: Set<Fn> }

function getOrCreate(key) {
  let entry = cache.get(key);
  if (!entry) {
    entry = { data: null, ts: 0, error: null, inFlight: null, subscribers: new Set() };
    cache.set(key, entry);
  }
  return entry;
}

function notify(entry) {
  for (const fn of entry.subscribers) {
    try { fn(); } catch { /* subscriber crash must not affect others */ }
  }
}

async function fetchAndStore(key) {
  const entry = getOrCreate(key);
  if (entry.inFlight) return entry.inFlight;
  entry.inFlight = (async () => {
    try {
      const data = await apiClient.request(key);
      entry.data = data;
      entry.ts = Date.now();
      entry.error = null;
    } catch (e) {
      entry.error = e;
      // Preserve `data` — last-known-good is more useful than nothing.
    } finally {
      entry.inFlight = null;
      notify(entry);
    }
    return entry;
  })();
  return entry.inFlight;
}

export const useAdminHealthCache = (key, { pollMs = 60000 } = {}) => {
  const entry = getOrCreate(key);
  const [, forceRender] = useState(0);
  const mountedRef = useRef(true);

  const bump = useCallback(() => {
    if (mountedRef.current) forceRender((n) => n + 1);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    entry.subscribers.add(bump);

    // Initial fetch — but respect TTL so a component mounting inside 30s
    // of the last fetch reads instantly from cache without a network round-trip.
    const age = Date.now() - entry.ts;
    if (!entry.data || age > TTL_MS) {
      fetchAndStore(key);
    } else if (age > TTL_MS / 2) {
      // Stale-while-revalidate: data is fresh enough to render immediately,
      // but old enough that a quiet background refetch is worthwhile.
      fetchAndStore(key);
    }

    // Poll — but the shared in-flight guard means N components polling the
    // same key still fire only one network request per tick.
    const interval = setInterval(() => fetchAndStore(key), pollMs);
    return () => {
      mountedRef.current = false;
      entry.subscribers.delete(bump);
      clearInterval(interval);
    };
    // `entry` is a stable module-scoped ref; `bump` is memoised. Listing
    // them adds noise without changing behaviour.
  }, [key, pollMs]);

  const refetch = useCallback(() => fetchAndStore(key), [key]);

  return {
    data: entry.data,
    // On the synchronous first render `useEffect` hasn't fired yet, so
    // `entry.inFlight` is still null. If we returned `loading: false` at
    // that moment while `data` is also null, consumers like
    // `PostmarkBudgetCard` would fall through to their data-render branch
    // and dereference `data.total` → TypeError → whole /admin page down
    // (regression caught by testing_agent_v3_fork iteration_191).
    //
    // The hook contract must be: loading stays true until we have EITHER
    // real data OR a real error. That way every current + future consumer
    // is protected by the hook's own state machine.
    loading: !entry.data && !entry.error,
    error: entry.error,
    lastUpdated: entry.ts,
    refetch,
  };
};

// Test-only cache-clear helper. Not called from any production code.
export const __clearAdminHealthCache = () => cache.clear();

export default useAdminHealthCache;
